import { createContext, FC, PropsWithChildren, useContext, useState } from 'react';
import { Button, FormLabel, IconExternalLink, Input } from '@screentone/core';

import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { RIBBON_REGEX } from 'utils/constants';
import env from 'utils/env';
import styles from './page-ribbon.module.scss';

type PageRibbonContextValues = {
  url: string;
  actions: {
    onUrlChange: (url: string | null | undefined) => void;
    onRemoveUrl: () => void;
    isValidUrl: () => boolean;
    onAddUrl: () => void;
  };
};

export const PageRibbonContext = createContext<PageRibbonContextValues | undefined>(undefined);

export const PageRibbonProvider: FC<PropsWithChildren> = ({ children }) => {
  const { setSectionAttributes, getSectionAttributes } = useDataModelContext();
  const [url, setUrl] = useState<string>(() => {
    const { ribbonUrl } = getSectionAttributes();

    return ribbonUrl ?? '';
  });

  const handleUrlChange = (url: string | null | undefined) => {
    if (typeof url !== 'string') {
      return;
    }

    setUrl(url.trim());
  };

  const handleUrlRemoval = () => {
    setUrl('');
    setSectionAttributes({
      ...getSectionAttributes(),
      ribbonUrl: ''
    });
  };

  const onAddUrl = () => {
    setSectionAttributes({
      ...getSectionAttributes(),
      ribbonUrl: url
    });
  };

  const isValidUrl = () => {
    if (typeof url !== 'string') {
      return false;
    }

    return url.length > 3 && RIBBON_REGEX.test(url);
  };

  const value: PageRibbonContextValues = {
    url,
    actions: {
      onUrlChange: handleUrlChange,
      onRemoveUrl: handleUrlRemoval,
      isValidUrl,
      onAddUrl
    }
  };

  return <PageRibbonContext.Provider value={value}>{children}</PageRibbonContext.Provider>;
};

const usePageRibbonContext = () => {
  const context = useContext(PageRibbonContext);

  if (!context) {
    throw new Error('usePageRibbonContext must be a child of PageRibbonProvider');
  }

  return context;
};

export const PageRibbonField: FC<PropsWithChildren> = ({ children }) => {
  const {
    url,
    actions: { onUrlChange, onRemoveUrl, isValidUrl, onAddUrl }
  } = usePageRibbonContext();

  const { getSectionAttributes } = useDataModelContext();

  const { ribbonUrl } = getSectionAttributes();

  const canRibbonBeRemoved = typeof ribbonUrl === 'string' && ribbonUrl.length > 3;

  return (
    <div className={styles.ribbonContainer}>
      <FormLabel
        label="Page Ribbon"
        labelPosition="top"
        margin={{
          bottom: 'xs'
        }}
      />
      <div className={styles.ribbonInputs}>
        <Input
          type="url"
          name="narratorUrl"
          placeholder="Add JSON URL from Narrator"
          value={url}
          onChange={(e) => {
            e.preventDefault();
            onUrlChange(e.target.value);
          }}
          error={url.length > 0 && !isValidUrl()}
        />
        {canRibbonBeRemoved ? (
          <Button secondary onClick={onRemoveUrl}>
            Remove
          </Button>
        ) : (
          <Button secondary onClick={onAddUrl} disabled={!isValidUrl()}>
            Add
          </Button>
        )}
      </div>
      {children}
    </div>
  );
};

export const PageRibbonButton = () => {
  const { getSectionAttributes } = useDataModelContext();

  const { ribbonUrl } = getSectionAttributes();

  if (typeof ribbonUrl === 'string' && ribbonUrl.length > 3) {
    const id = ribbonUrl.split('/').pop()?.split('.json')[0];
    const narratorUrl = `${env.NARRATOR_TOOL}${id}`;

    return (
      <Button
        componentEl="a"
        href={narratorUrl}
        target="_blank"
        rel="noopener noreferrer"
        tertiary
        margin={{
          all: 'none',
          top: 'sm'
        }}
        icon={IconExternalLink}
        iconPosition="right"
        className={styles.ribbonButton}
      >
        View in Narrator
      </Button>
    );
  }

  return null;
};
